import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEmails, EmailMessage, EmailsOptions } from '@/hooks/useEmails';
import { useCategoryStore } from '@/store/categoryStore';
import { RefreshCw, Move, Trash2, MailCheck, Star, StarOff, Folder, SortAsc, SortDesc, RotateCw, MoreHorizontal, ChevronDown, PenSquare } from 'lucide-react';
import { FolderBadge } from '@/components/ui/FolderBadge';
import LoadingState from '@/components/ui/LoadingState';
import EmptyState from '@/components/ui/EmptyState';
import { starEmail, moveEmails, bulkStarEmails, bulkMarkReadEmails, bulkDeleteEmails, deleteEmail } from '@/services/api';
import { useFolderStore } from '@/store/folderStore';
import { usePagination } from '@/contexts/PaginationContext';
import toast from 'react-hot-toast';
import { EMAIL_UPDATE_EVENT } from '@/hooks/useFirebaseNotification';
import { SYNC_COMPLETE_EVENT } from '@/hooks/useEmails';
import api from '@/services/api';
import { Modal, ModalContent, ModalTitle } from '@/components/ui/Modal';
import { format } from 'date-fns';
import { formatEmailListDate } from '@/utils/emailDate';
import './InboxList.css';
// import { authService } from '@/services/auth';
// import { Tooltip } from '@/components/ui/Tooltip';

interface InboxListProps {
  folderName?: string;
  onCompose?: () => void;
  onPaginationChange?: (paginationState: {
    currentPage: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    totalEmails: number;
    emailsPerPage: number;
  }) => void;
}

export interface InboxListRef {
  handlePageChange: (page: number) => void;
}

// Utility function to extract plain text from HTML
const extractTextFromHTML = (html: string): string => {
  if (!html) return '';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Get text content and clean it up
  let text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Remove extra whitespace and normalize
  text = text.replace(/\s+/g, ' ').trim();
  
  // Remove common email reply patterns
  text = text.replace(/On .+ wrote:.+|From:.+|Sent:.+|To:.+|Subject:.+/gis, '');
  
  return text;
};

interface BulkMarkReadResult {
  uid: number;
  success: boolean;
  flags: string[];
}

interface BulkMarkReadResponse {
  success: boolean;
  data: {
    success: boolean;
    operation: string;
    method: string;
    folderPath: string;
    total: number;
    successful: number;
    failed: number;
    errors: any[];
    results: BulkMarkReadResult[];
    dbUpdated: boolean;
    dbUpdateCount: number;
  };
}

// Add LoaderIcon component at the top of the file
// const LoaderIcon = ({ size = 18, color = '#3498db' }) => (
//   <div className="loading-spinner" style={{ 
//     width: `${size}px`, 
//     height: `${size}px`,
//     border: `2px solid #f3f3f3`,
//     borderTop: `2px solid ${color}`,
//     borderRadius: '50%',
//     animation: 'spin 1s linear infinite'
//   }} />
// );

//   const LoadingSpinner = () => (
//   <div className="loading-spinner" style={{ 
//     width: '18px', 
//     height: '18px',
//     border: '2px solid #f3f3f3',
//     borderTop: '2px solid #3498db',
//     borderRadius: '50%',
//     animation: 'spin 1s linear infinite',
//     marginRight: '8px'
//   }} />
// );

// Add this helper function at the top level
const ensureArray = (flags: any): string[] => {
  if (!flags) return [];
  if (Array.isArray(flags)) return flags;
  if (typeof flags === 'string') return [flags];
  return [];
};

const InboxList = forwardRef<InboxListRef, InboxListProps>(({ folderName = 'INBOX', onCompose, onPaginationChange }, ref) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allEmails, setAllEmails] = useState<EmailMessage[]>([]);
  const { selectedCategory, categoryEmails } = useCategoryStore();
  const { currentFolder, setCurrentFolder } = useFolderStore();
  const { setPaginationState, registerPageChangeHandler } = usePagination();
  const { decrementUnreadCount } = useFolderStore();
  
  // Page comes from the URL — single source of truth for API pagination
  const page = useMemo(() => {
    const rawPage = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  }, [searchParams]);
  const [searchTerm] = useState(searchParams.get('search') || '');
  const [sortBy] = useState<EmailsOptions['sortBy']>((searchParams.get('sortBy') as EmailsOptions['sortBy']) || 'date');
  const [sortOrder, setSortOrder] = useState<EmailsOptions['sortOrder']>((searchParams.get('sortOrder') as EmailsOptions['sortOrder']) || 'desc');
  const [showUnread, setShowUnread] = useState<boolean>(searchParams.get('isUnread') === 'true');
  
  // Determine the active folder: route param > prop > store current folder
  const activeFolder = params.folderName || folderName || currentFolder;
  
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentAction, setCurrentAction] = useState<'refresh' | 'sync'>('refresh');

  // Debounce search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Add syncType state
  const [syncType, setSyncType] = useState<'all' | 'last' | null>(null);

  // Format folder name
  const formattedFolder = activeFolder === 'inbox' ? 'INBOX' : activeFolder;
  
  const { emails, loading, error, pagination, retry: retryFn, retryCount, isSyncing } = useEmails(
    formattedFolder,
    page,
    20,
    selectedCategory || undefined,
    refreshKey,
    {
      sortBy,
      sortOrder,
      search: debouncedSearchTerm,
      action: currentAction,
      all: syncType === 'all',
      unread: showUnread
    }
  );

  interface EmailUpdateEvent extends CustomEvent {
    detail: {
      type: string;
      folder: string;
      email_data?: EmailMessage;
      email_id?: string;
      email_uid?: string;
      newCount?: string;
      totalCount?: string;
      unseenCount?: string;
    };
  }

  // Listen for email updates and sync completion
  useEffect(() => {
    const handleEmailUpdate = (event: Event) => {
      const customEvent = event as EmailUpdateEvent;
      const { type, folder, email_data } = customEvent.detail;

      // Only update if it's a new email for the current folder
      if (type === "new_messages" && folder === formattedFolder) {
        // If we have the new email data directly from the event
        if (email_data) {
          setAllEmails(prevEmails => [email_data, ...prevEmails]);
          return;
        }

        // Fallback to localStorage if no direct email data
        const localKey = `emails_${folder}_1`;
        const cached = localStorage.getItem(localKey);
        
        if (cached) {
          try {
            const parsedCache = JSON.parse(cached);
            setAllEmails(parsedCache.emails || []);
          } catch (err) {
            console.error('Failed to parse cached emails:', err);
          }
        }

        // Trigger a refresh to ensure we have the latest data
        setRefreshKey(prev => prev + 1);
      }
    };

    const handleSyncComplete = (event: CustomEvent) => {
      const { folderName: syncedFolder, success } = event.detail;
      if (success && syncedFolder === formattedFolder) {
        // Reset sync state
        setSyncType(null);
        setCurrentAction('refresh');
        // Trigger a refresh
        setRefreshKey(prev => prev + 1);
      }
    };

    window.addEventListener(EMAIL_UPDATE_EVENT, handleEmailUpdate as EventListener);
    window.addEventListener(SYNC_COMPLETE_EVENT, handleSyncComplete as EventListener);
    return () => {
      window.removeEventListener(EMAIL_UPDATE_EVENT, handleEmailUpdate as EventListener);
      window.removeEventListener(SYNC_COMPLETE_EVENT, handleSyncComplete as EventListener);
    };
  }, [formattedFolder]);

  // Sync unread filter from URL
  useEffect(() => {
    const urlUnread = searchParams.get('isUnread') === 'true';
    if (urlUnread !== showUnread) {
      setShowUnread(urlUnread);
      setRefreshKey((k) => k + 1);
    }
  }, [searchParams, showUnread]);

  // Use synced emails if available, otherwise use refreshed emails
  const emailsList = emails;
  const isLoading = loading;
  const errorState = error;
  const combinedPagination = pagination;

  // Determine which emails and pagination to use
  const currentEmails = selectedCategory && categoryEmails && categoryEmails.emails ? categoryEmails.emails : emailsList;
  const currentPagination = selectedCategory && categoryEmails && categoryEmails.pagination ? categoryEmails.pagination : combinedPagination;
  const currentLoading = selectedCategory ? false : isLoading;
  const currentError = selectedCategory ? null : errorState;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedUids, setSelectedUids] = useState<number[]>([]);
  const { folders } = useFolderStore();
  const [showMoveModal, setShowMoveModal] = useState(false);

  const [singleMoveUid, setSingleMoveUid] = useState<number | null>(null);
  const [movingFolder, setMovingFolder] = useState<string | null>(null);

  // Add these state variables at the top of the component
  const [actionLoadingStates, setActionLoadingStates] = useState<{[key: string]: { star?: boolean; unstar?: boolean; read?: boolean; move?: boolean; delete?: boolean }}>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [showSyncOptions, setShowSyncOptions] = useState(false);

  // Get the current folder's last sync time
  // console.log(folders);
  const currentFolderData = folders.find(f => f.name === formattedFolder);
  const lastSyncTime = currentFolderData?.last_synced 
    ? format(new Date(currentFolderData.last_synced), 'MMM d, yyyy h:mm a')
    : 'Never';

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showBulkMenu && !(event.target as Element).closest('.bulk-menu')) {
        setShowBulkMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showBulkMenu]);

  // Helper function to set loading state for a specific action
  const setActionLoading = (uid: number, action: 'star' | 'unstar' | 'read' | 'move' | 'delete', isLoading: boolean) => {
    setActionLoadingStates(prev => ({
      ...prev,
      [uid]: {
        ...(prev[uid] || {}),
        [action]: isLoading
      }
    }));
  };

  // Handle folder changes. Driven only by the route so a store update that
  // renders before a pending navigation cannot redirect back to the old folder.
  const prevActiveFolderRef = useRef(activeFolder);
  useEffect(() => {
    if (!activeFolder) return;

    if (useFolderStore.getState().currentFolder !== activeFolder) {
      setCurrentFolder(activeFolder);
    }

    if (prevActiveFolderRef.current === activeFolder) return;
    prevActiveFolderRef.current = activeFolder;

    // Reset states
    setAllEmails([]);

    // Reset pagination state
    setPaginationState({
      currentPage: 1,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
      totalEmails: 0,
      emailsPerPage: 20
    });

    // Trigger refresh
    setRefreshKey((k) => k + 1);
  }, [activeFolder, setCurrentFolder, setPaginationState]);

  // Reset page when category changes
  useEffect(() => {
    if (selectedCategory) {
      setAllEmails([]);
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('page', '1');
        return newParams;
      });
    }
  }, [selectedCategory, setSearchParams]);

  // Fetch category emails when category is selected
  // useEffect(() => {
  //   if (selectedCategory) {
  //     fetchEmailsForCategory(selectedCategory, page, 20);
  //   }
  // }, [selectedCategory, page, fetchEmailsForCategory]);

  // Keep displayed emails in sync with hook data
  useEffect(() => {
    if (Array.isArray(currentEmails)) {
      setAllEmails(currentEmails);
    }
  }, [currentEmails]);

  const formatDate = (email: EmailMessage) => {
    // Use lastReply date if available, otherwise fall back to email's date
    return formatEmailListDate(email.lastReply?.date || email.envelope.date);
  };

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    if (!name || typeof name !== 'string') return '#f59e0b';
    const colors = ['#f59e0b', '#059669', '#dc2626', '#8d00e4', '#7c3aed', '#0891b2'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const isUnread = (email: EmailMessage) => {
    // Don't mark as unread in Sent folder
    if (activeFolder === 'Sent') return false;
    
    let new_flags = JSON.stringify(email.lastReply?.flags ? email.lastReply.flags : email.flags);

    //use a regular expression to check if the email is unread
    const unreadRegex = /\\Seen/;
    if (unreadRegex.test(new_flags)) {
      return false;
    }
    
    // If lastReply exists, use its flags
    if (email.lastReply) {
      const flags = ensureArray(new_flags);
      return flags.length === 0 || !flags.includes('\\Seen');
    }
    
    // Otherwise use email flags
    const flags = ensureArray(new_flags);
    // If flags array is empty, treat as unread
    if (flags.length === 0) return true;
    return !flags.includes('\\Seen');
  };

  const hasAttachments = (email: EmailMessage) => {
    return email.attachments && Array.isArray(email.attachments) && email.attachments.length > 0;
  };

  const handleEmailClick = (email: EmailMessage) => {
    // Navigate to email chat page with the email data
    if (email.mainEmail?.id) {
      navigate(`/email/${email.mainEmail.folder.name}/${email.mainEmail.id}/${email.mainEmail.uid}`, {
        state: { emailInitial: email }
      });
    } else {
      navigate(`/email/${activeFolder}/${email.id}/${email.uid}`, {
        state: { emailInitial: email }
      });
    }

    // navigate(`/email/${activeFolder}/${email.id}`, {
    //   state: { emailInitial: email }
    // });
  };

  // Update pagination context state
  useEffect(() => {
    if (currentPagination) {
      const maxPage = Math.max(1, currentPagination.totalPages || 1);
      const safePage = Math.min(Math.max(1, page), maxPage);
      const newState = {
        currentPage: safePage,
        totalPages: maxPage,
        hasNext: Boolean(currentPagination.hasNext) && safePage < maxPage,
        hasPrev: safePage > 1,
        totalEmails: currentPagination.total || 0,
        emailsPerPage: 20
      };
      setPaginationState((prev) => {
        if (
          prev.currentPage === newState.currentPage &&
          prev.totalPages === newState.totalPages &&
          prev.hasNext === newState.hasNext &&
          prev.hasPrev === newState.hasPrev &&
          prev.totalEmails === newState.totalEmails
        ) {
          return prev;
        }
        return newState;
      });
      
      if (onPaginationChange) {
        onPaginationChange(newState);
      }
    }
  }, [currentPagination, page, onPaginationChange, setPaginationState]);

  // Clamp out-of-range URL pages (e.g. ?page=12) to the last valid page
  useEffect(() => {
    if (!currentPagination?.totalPages) return;
    const maxPage = Math.max(1, currentPagination.totalPages);
    if (page <= maxPage) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('page') === String(maxPage)) return prev;
      next.set('page', String(maxPage));
      return next;
    });
  }, [currentPagination?.totalPages, page, setSearchParams]);

  // Update URL when search or sort changes
  useEffect(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (searchTerm) {
        newParams.set('search', searchTerm);
      } else {
        newParams.delete('search');
      }
      if (sortBy) {
        newParams.set('sortBy', sortBy);
      }
      if (sortOrder) {
        newParams.set('sortOrder', sortOrder);
      }
      return newParams;
    });
  }, [searchTerm, sortBy, sortOrder, setSearchParams]);

  // Reset page when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== searchParams.get('search')) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('page', '1');
        return newParams;
      });
    }
  }, [debouncedSearchTerm, searchParams, setSearchParams]);

  // Local handlePageChange — updates URL only; page is derived from searchParams
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || (currentPagination && newPage > currentPagination.totalPages)) {
      return;
    }

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', newPage.toString());
      return newParams;
    });

    setPaginationState(prev => ({ ...prev, currentPage: newPage }));

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentPagination, setSearchParams, setPaginationState]);

  // Route EmailCategory pagination through the same handler (one fetch per page change)
  useEffect(() => {
    registerPageChangeHandler(handlePageChange);
    return () => registerPageChangeHandler(null);
  }, [handlePageChange, registerPageChangeHandler]);

  // Expose handlePageChange function to parent component (for backward compatibility)
  useImperativeHandle(ref, () => ({
    handlePageChange: handlePageChange
  }), [handlePageChange]);

  // Selection logic
  const isAllSelected = allEmails.length > 0 && selectedUids.length === allEmails.length;
  const isIndeterminate = selectedUids.length > 0 && selectedUids.length < allEmails.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUids([]);
    } else {
      setSelectedUids(allEmails.map(e => e.uid));
    }
  };

  const handleSelectOne = (uid: number) => {
    setSelectedUids(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleMove = async (targetFolderName: string) => {
    if (!selectedUids.length) return;
    
    try {
      selectedUids.forEach(uid => setActionLoading(uid, 'move', true));
      await moveEmails({
        sourceFolder: activeFolder,
        targetFolder: targetFolderName,
        uids: selectedUids,
      });
      toast.success('Emails moved successfully');
      setSelectedUids([]);
      setShowMoveModal(false);
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      toast.error('Failed to move emails: ' + (err?.message || 'Unknown error'));
      throw err; // Re-throw to handle in the folder click handler
    } finally {
      selectedUids.forEach(uid => setActionLoading(uid, 'move', false));
    }
  };

  const handleSingleMove = async (uid: number, targetFolderName: string) => {
    try {
      setActionLoading(uid, 'move', true);
      await moveEmails({
        sourceFolder: activeFolder,
        targetFolder: targetFolderName,
        uids: [uid],
      });
      toast.success('Email moved successfully');
      setAllEmails(prev => prev.filter(e => e.uid !== uid));
      setShowMoveModal(false);
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      toast.error('Failed to move email: ' + (err?.message || 'Unknown error'));
      throw err; // Re-throw to handle in the folder click handler
    } finally {
      setActionLoading(uid, 'move', false);
    }
  };

  interface BulkStarResponse {
    success: boolean;
    data: {
      success: boolean;
      operation: string;
      method: string;
      folderPath: string;
      total: number;
      successful: number;
      failed: number;
      errors: any[];
      results: Array<{
        uid: number;
        success: boolean;
        flags: string[];
      }>;
      dbUpdated: boolean;
      dbUpdateCount: number;
    };
  }

  const handleBulkStar = async (starred: boolean) => {
    if (selectedUids.length === 0) return;
    try {
      selectedUids.forEach(uid => setActionLoading(uid, 'star', true));
      const { data: response } = await bulkStarEmails({
        folderPath: activeFolder,
        uids: selectedUids,
        starred,
      });

      const { data } = response as BulkStarResponse;
      if (data.success) {
        const totalStarred = data.successful;
        toast.success(
          `${totalStarred} ${totalStarred === 1 ? 'email' : 'emails'} ${starred ? 'starred' : 'unstarred'}`
        );

        // Update UI with successful UIDs
        const successfulUids = data.results.filter(r => r.success).map(r => r.uid);
        setAllEmails(prev => prev.map(e => {
          if (!successfulUids.includes(e.uid)) return e;
          
          // Ensure flags is always an array
          const currentFlags = Array.isArray(e.flags) ? e.flags : [];
          
          if (starred) {
            return {
              ...e,
              flags: [...new Set([...currentFlags, '\\Flagged'])]
            };
          } else {
            return {
              ...e,
              flags: currentFlags.filter(f => f !== '\\Flagged')
            };
          }
        }));
      }
      
      setSelectedUids([]);
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      toast.error('Failed to update star: ' + (err?.message || 'Unknown error'));
    } finally {
      selectedUids.forEach(uid => setActionLoading(uid, 'star', false));
    }
  };

  const handleBulkMarkRead = async () => {
    // Don't mark as read in Sent folder
    if (activeFolder === 'Sent') return;

    if (selectedUids.length === 0) return;
    try {
      selectedUids.forEach(uid => setActionLoading(uid, 'read', true));
      const { data: response } = await bulkMarkReadEmails({
        folderPath: activeFolder,
        uids: selectedUids,
        read: true,
      });

      const { data } = response as BulkMarkReadResponse;
      if (data.success) {
        const totalMarked = data.successful;
        toast.success(`${totalMarked} ${totalMarked === 1 ? 'message' : 'messages'} marked as read`);
        
        // Update UI with successful UIDs
        const successfulUids = data.results.filter(r => r.success).map(r => r.uid);
      setAllEmails(prev => prev.map(e =>
          successfulUids.includes(e.uid)
            ? { ...e, flags: [...new Set([...e.flags, '\\Seen'])] }
          : e
      ));
      }
      
      setSelectedUids([]);
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      // Handle error silently
    } finally {
      selectedUids.forEach(uid => setActionLoading(uid, 'read', false));
    }
  };

  interface BulkDeleteResponse {
    success: boolean;
    data: {
      success: boolean;
      operation: string;
      method: string;
      sourceFolder: string;
      destinationFolder: string;
      total: number;
      successful: number;
      failed: number;
      errors: any[];
      results: Array<{
        uid: number;
        success: boolean;
        seqno: number;
      }>;
      dbUpdated: boolean;
      dbUpdateCount: number;
      oldFolderId: string;
      newFolderId: string;
    };
  }

  const handleBulkDelete = async () => {
    if (selectedUids.length === 0) return;
    try {
      selectedUids.forEach(uid => setActionLoading(uid, 'delete', true));
      const { data: response } = await bulkDeleteEmails({
        folderPath: activeFolder,
        uids: selectedUids,
      });

      const { data } = response as BulkDeleteResponse;
      if (data.success) {
        const totalDeleted = data.successful;
        toast.success(
          `${totalDeleted} ${totalDeleted === 1 ? 'email' : 'emails'} moved to ${data.destinationFolder}`
        );

        // Update UI with successful UIDs
        const successfulUids = data.results.filter(r => r.success).map(r => r.uid);
        setAllEmails(prev => prev.filter(e => !successfulUids.includes(e.uid)));
      }

      setSelectedUids([]);
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      toast.error('Failed to delete emails: ' + (err?.message || 'Unknown error'));
    } finally {
      selectedUids.forEach(uid => setActionLoading(uid, 'delete', false));
    }
  };

  const handleSingleDelete = async (email: EmailMessage) => {
    try {
      await deleteEmail(activeFolder, email.id, email.uid);
      setAllEmails(prev => prev.filter(e => e.uid !== email.uid));
      // fetch the emails again
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      toast.error('Failed to delete: ' + (err?.message || 'Unknown error'));
    }
  };

  const handleSingleMarkRead = async (uid: number, id: string) => {
    // Don't mark as read in Sent folder
    if (activeFolder === 'Sent') return;

    try {
      // Call the mark as read endpoint
      await api.put(`/api/email/message/${folderName}/${id}/${uid}/read`);

      // Update local state
      setAllEmails(prev => prev.map(email => {
        if (email.uid === uid && !email.flags.includes('\\Seen')) {
          return { ...email, flags: [...email.flags, '\\Seen'] };
        }
        return email;
      }));

      // Decrement unread count in folder store
      decrementUnreadCount(folderName);

      // Update localStorage cache for this email in the folder/page
      // Try to update all possible pages (for infinite scroll)
      for (let page = 1; page <= 10; page++) {
        const localKey = `emails_${folderName}_${page}`;
        const cached = localStorage.getItem(localKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            let updated = false;
            const updatedEmails = (parsed.emails || []).map((msg: any) => {
              if (msg.uid === uid && !msg.flags.includes('\\Seen')) {
                updated = true;
                return { ...msg, flags: [...msg.flags, '\\Seen'] };
              }
              return msg;
            });
            if (updated) {
              localStorage.setItem(localKey, JSON.stringify({ ...parsed, emails: updatedEmails }));
            }
          } catch { }
        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // Extract the options toolbar into a separate component
  const OptionsToolbar = () => (
    <div className="app-inbox__toolbar">
      <input
        type="checkbox"
        className="app-inbox__checkbox"
        checked={isAllSelected}
        ref={el => { if (el) el.indeterminate = isIndeterminate; }}
        onChange={handleSelectAll}
        aria-label="Select all emails"
      />
      <div className="app-inbox__toolbar-actions">
        <button
          type="button"
          className="app-inbox__toolbar-btn"
          onClick={() => {
            setCurrentAction('refresh');
            setRefreshKey(k => k + 1);
            toast.success('Refreshed emails');
          }}
          disabled={loading}
          title="Refresh emails"
        >
          <RefreshCw size={12} style={{ animation: loading && !isSyncing ? 'inbox-spin 1s linear infinite' : 'none' }} />
          {!isMobile && 'Refresh'}
        </button>

        <button
          type="button"
          className="app-inbox__toolbar-btn"
          onClick={() => setShowSyncOptions(true)}
          disabled={loading}
          title="Sync folder"
        >
          <RotateCw size={12} style={{ animation: loading && isSyncing ? 'inbox-spin 1s linear infinite' : 'none' }} />
          {!isMobile && 'Sync'}
        </button>

        <button
          type="button"
          className={`app-inbox__toolbar-btn${showUnread ? ' app-inbox__toolbar-btn--active' : ''}`}
          onClick={() => {
            setShowUnread(prev => !prev);
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set('isUnread', (!showUnread).toString());
              return newParams;
            });
            setRefreshKey(Date.now());
          }}
        >
          <MailCheck size={12} />
          {!isMobile && <span style={{ marginLeft: 4 }}>Unread</span>}
        </button>

        <button
          type="button"
          className="app-inbox__toolbar-btn"
          onClick={() => setSortOrder(current => current === 'asc' ? 'desc' : 'asc')}
        >
          {sortOrder === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />}
          {!isMobile && <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? 'Oldest' : 'Newest'}</span>}
        </button>
      </div>
    </div>
  );

  // Show loading state for initial load
  if (loading && allEmails.length === 0) {
    return (
      <>
        <div style={{ width: '100%', height: '100%', display: 'block' }}>
          <OptionsToolbar />
          <LoadingState folderName={folderName} type="skeleton" />
        </div>
        {/* Sync Options Modal */}
        <Modal open={showSyncOptions} onOpenChange={setShowSyncOptions}>
          <ModalContent>
            <ModalTitle>Sync Options</ModalTitle>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    setSyncType('last');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing from last sync...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RotateCw size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync from Last Sync
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Only sync new emails since your last synchronization
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#8d00e4',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>Last synced: {lastSyncTime}</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSyncType('all');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing all emails...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RefreshCw size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync All Emails
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Synchronize all emails in this folder
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Show error state with retry functionality
  if (currentError && allEmails.length === 0) {
    const isTimeoutError = currentError.includes('timed out') || currentError.includes('timeout');
    
    return (
      <>
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <OptionsToolbar />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            width: '100%',
            minHeight: '400px',
            padding: '40px 20px',
            margin: '24px',
            flexDirection: 'column',
            gap: '16px',
            background: '#ffffff80',
            borderRadius: '12px',
            border: '1px solid #fecaca'
          }}>
            <div style={{ fontSize: '48px' }}>⚠️</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626', textAlign: 'center' }}>
              {isTimeoutError ? 'Request Timed Out' : 'Error Loading Emails'}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', maxWidth: '400px', lineHeight: '1.5' }}>
              {currentError}
            </div>
            
            {retryCount > 0 && (
              <div className="retry-message">
                Retry attempt {retryCount}/3
              </div>
            )}
            
            <button
              onClick={retryFn}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        </div>
        {/* Sync Options Modal */}
        <Modal open={showSyncOptions} onOpenChange={setShowSyncOptions}>
          <ModalContent>
            <ModalTitle>Sync Options</ModalTitle>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    setSyncType('last');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing from last sync...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RotateCw size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync from Last Sync
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Only sync new emails since your last synchronization
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#8d00e4',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>Last synced: {lastSyncTime}</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSyncType('all');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing all emails...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RefreshCw size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync All Emails
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Synchronize all emails in this folder
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Show empty state
  if (!currentLoading && !currentError && currentEmails && currentEmails.length === 0 && allEmails.length === 0) {
    return (
      <>
        <div style={{ width: '100%', height: '100%', display: 'block' }}>
          <OptionsToolbar />
          <EmptyState folderName={activeFolder} onCompose={onCompose} />
        </div>
        {/* Sync Options Modal */}
        <Modal open={showSyncOptions} onOpenChange={setShowSyncOptions}>
          <ModalContent>
            <ModalTitle>Sync Options</ModalTitle>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    setSyncType('last');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing from last sync...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RotateCw size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync from Last Sync
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Only sync new emails since your last synchronization
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#8d00e4',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>Last synced: {lastSyncTime}</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setSyncType('all');
                    setCurrentAction('sync');
                    setRefreshKey(k => k + 1);
                    setShowSyncOptions(false);
                    toast.success('Syncing all emails...');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#8d00e4'
                  }}>
                    <RefreshCw size={16} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                      Sync All Emails
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Synchronize all emails in this folder
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="app-inbox"
    >
      {/* Bulk Actions Toolbar */}
      {selectedUids.length > 0 && (
        <div className="app-inbox__bulk-toolbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="checkbox"
              className="app-inbox__checkbox"
              checked={selectedUids.length > 0}
              onChange={handleSelectAll}
              aria-label="Select all emails"
            />
            <span className="app-inbox__bulk-count">
              {selectedUids.length} selected
            </span>
          </div>

          {isMobile ? (
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBulkMenu(!showBulkMenu);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: showBulkMenu ? '#f3f4f6' : '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Actions
                <ChevronDown size={16} style={{
                  transform: showBulkMenu ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease'
                }} />
              </button>

              {showBulkMenu && (
                <div 
                  className="bulk-menu"
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid #e5e7eb',
                    width: '200px',
                    overflow: 'hidden',
                    zIndex: 30
                  }}
                >
                  <button
                    onClick={() => {
                      handleBulkMarkRead();
                      setShowBulkMenu(false);
                    }}
                    disabled={actionLoadingStates[selectedUids[0]]?.read}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      borderBottom: '1px solid #e5e7eb',
                      background: 'none',
                      cursor: actionLoadingStates[selectedUids[0]]?.read ? 'not-allowed' : 'pointer',
                      color: '#374151',
                      fontSize: '14px',
                      textAlign: 'left',
                      opacity: actionLoadingStates[selectedUids[0]]?.read ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[selectedUids[0]]?.read ? (
                      <div className="loading-spinner" style={{ 
                        width: '18px', 
                        height: '18px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : <MailCheck size={18} />}
                    Mark as Read
                  </button>

                  <button
                    onClick={() => {
                      handleBulkStar(!isAllSelected);
                      setShowBulkMenu(false);
                    }}
                    disabled={actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      borderBottom: '1px solid #e5e7eb',
                      background: 'none',
                      cursor: (actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? 'not-allowed' : 'pointer',
                      color: '#374151',
                      fontSize: '14px',
                      textAlign: 'left',
                      opacity: (actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? 0.7 : 1
                    }}
                  >
                    {(actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? (
                      <div className="loading-spinner" style={{ 
                        width: '18px', 
                        height: '18px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : isAllSelected ? <StarOff size={18} /> : <Star size={18} />}
                    {isAllSelected ? 'Unstar' : 'Star'}
                  </button>

                  <button
                    onClick={() => {
                      setShowMoveModal(true);
                      setShowBulkMenu(false);
                    }}
                    disabled={actionLoadingStates[selectedUids[0]]?.move}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      borderBottom: '1px solid #e5e7eb',
                      background: 'none',
                      cursor: actionLoadingStates[selectedUids[0]]?.move ? 'not-allowed' : 'pointer',
                      color: '#374151',
                      fontSize: '14px',
                      textAlign: 'left',
                      opacity: actionLoadingStates[selectedUids[0]]?.move ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[selectedUids[0]]?.move ? (
                      <div className="loading-spinner" style={{ 
                        width: '18px', 
                        height: '18px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : <Move size={18} />}
                    Move to
                  </button>

                  <button
                    onClick={() => {
                      handleBulkDelete();
                      setShowBulkMenu(false);
                    }}
                    disabled={actionLoadingStates[selectedUids[0]]?.delete}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: actionLoadingStates[selectedUids[0]]?.delete ? 'not-allowed' : 'pointer',
                      color: '#dc2626',
                      fontSize: '14px',
                      textAlign: 'left',
                      opacity: actionLoadingStates[selectedUids[0]]?.delete ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[selectedUids[0]]?.delete ? (
                      <div className="loading-spinner" style={{ 
                        width: '18px', 
                        height: '18px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #dc2626',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : <Trash2 size={18} />}
                    Delete
                  </button>
        </div>
      )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => handleBulkMarkRead()}
                disabled={actionLoadingStates[selectedUids[0]]?.read}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: actionLoadingStates[selectedUids[0]]?.read ? 'not-allowed' : 'pointer',
                  opacity: actionLoadingStates[selectedUids[0]]?.read ? 0.7 : 1
                }}
              >
                {actionLoadingStates[selectedUids[0]]?.read ? (
                  <div className="loading-spinner" style={{ 
                    width: '16px', 
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : <MailCheck size={16} />}
                Mark as Read
              </button>

              <button
                onClick={() => handleBulkStar(!isAllSelected)}
                disabled={actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: (actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? 'not-allowed' : 'pointer',
                  opacity: (actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? 0.7 : 1
                }}
              >
                {(actionLoadingStates[selectedUids[0]]?.star || actionLoadingStates[selectedUids[0]]?.unstar) ? (
                  <div className="loading-spinner" style={{ 
                    width: '16px', 
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : isAllSelected ? <StarOff size={16} /> : <Star size={16} />}
                {isAllSelected ? 'Unstar' : 'Star'}
              </button>

              <button
                onClick={() => setShowMoveModal(true)}
                disabled={actionLoadingStates[selectedUids[0]]?.move}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: actionLoadingStates[selectedUids[0]]?.move ? 'not-allowed' : 'pointer',
                  opacity: actionLoadingStates[selectedUids[0]]?.move ? 0.7 : 1
                }}
              >
                {actionLoadingStates[selectedUids[0]]?.move ? (
                  <div className="loading-spinner" style={{ 
                    width: '16px', 
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : <Move size={16} />}
                Move to
              </button>

              <button
                onClick={() => handleBulkDelete()}
                disabled={actionLoadingStates[selectedUids[0]]?.delete}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#dc2626',
                  fontSize: '14px',
                  cursor: actionLoadingStates[selectedUids[0]]?.delete ? 'not-allowed' : 'pointer',
                  opacity: actionLoadingStates[selectedUids[0]]?.delete ? 0.7 : 1
                }}
              >
                {actionLoadingStates[selectedUids[0]]?.delete ? (
                  <div className="loading-spinner" style={{ 
                    width: '16px', 
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #dc2626',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : <Trash2 size={16} />}
                Delete
              </button>
            </div>
          )}
        </div>
      )}
      <div className={`email-list app-inbox__list${isMobile ? ' app-inbox__list--mobile' : ''}`}>
        {currentLoading && allEmails.length && currentAction !== 'sync' && currentAction !== 'refresh' && (
          <div className="app-inbox__loading-overlay">
            <div className="app-inbox__loading-card">
              <div className="loading-spinner" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f1f3f4',
                borderTop: '2px solid #8d00e4',
                borderRadius: '50%',
              }} />
              <span>Loading emails...</span>
            </div>
          </div>
        )}
        <div className="selected-email app-inbox__toolbar">
          <input
            type="checkbox"
            className="app-inbox__checkbox"
            checked={isAllSelected}
            ref={el => { if (el) el.indeterminate = isIndeterminate; }}
            onChange={handleSelectAll}
            aria-label="Select all emails"
          />
          <div className="app-inbox__toolbar-actions">
            <button
              type="button"
              className="app-inbox__toolbar-btn"
              onClick={() => {
                setCurrentAction('refresh');
                setRefreshKey(k => k + 1);
                toast.success('Refreshed emails');
              }}
              disabled={loading}
              title="Refresh emails"
            >
              <RefreshCw size={12} style={{ animation: loading && !isSyncing ? 'inbox-spin 1s linear infinite' : 'none' }} />
              {!isMobile && 'Refresh'}
            </button>

            <button
              type="button"
              className="app-inbox__toolbar-btn"
              onClick={() => setShowSyncOptions(true)}
              disabled={loading}
              title="Sync folder"
            >
              <RotateCw size={12} style={{ animation: loading && isSyncing ? 'inbox-spin 1s linear infinite' : 'none' }} />
              {!isMobile && 'Sync'}
            </button>

            <button
              type="button"
              className={`app-inbox__toolbar-btn${showUnread ? ' app-inbox__toolbar-btn--active' : ''}`}
              onClick={() => {
                setShowUnread(prev => !prev);
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('isUnread', (!showUnread).toString());
                  return newParams;
                });
                setRefreshKey(Date.now());
              }}
            >
              <MailCheck size={12} />
              {!isMobile && <span style={{ marginLeft: 4 }}>Unread</span>}
            </button>

            <button
              type="button"
              className="app-inbox__toolbar-btn"
              onClick={() => setSortOrder(current => current === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />}
              {!isMobile && <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? 'Oldest' : 'Newest'}</span>}
            </button>
          </div>
        </div>
        {allEmails.map((email, index) => {
          const sender = email.envelope?.from?.[0] || { name: '', mailbox: '', host: '' };
          const senderName = email.lastReply ? email.lastReply.from.name : (sender.name || (sender.mailbox && sender.host ? `${sender.mailbox}@${sender.host}` : 'Unknown Sender'));
          const toName = email.envelope?.to?.[0]?.name || (email.envelope?.to?.[0]?.mailbox && email.envelope?.to?.[0]?.host ? `${email.envelope?.to?.[0]?.mailbox}@${email.envelope?.to?.[0]?.host}` : 'Unknown Recipient');
          const initials = getInitials(email.lastReply ? email.lastReply.from.name : senderName);
          const avatarColor = getAvatarColor(email.lastReply ? email.lastReply.from.name : senderName);
          const avatarUrl = `https://placehold.co/32x32/${avatarColor.replace('#', '')}/white?text=${initials}`;
          const flags = ensureArray(email.flags);
          const unread = isUnread(email);
          const isStarred = flags.includes('\\Flagged');
          const hasAttachment = hasAttachments(email);
          const showReadStatus = activeFolder !== 'Sent';
          
          return (
            <div
              className={`email-item email-list-item-height${unread ? ' unread' : ''}${selectedUids.includes(email.uid) ? ' email-item--selected' : ''}`}
              key={`${activeFolder}-${email.uid}-${index}`}
            >
              <div className={isMobile ? 'app-inbox__item-header' : undefined} style={isMobile ? undefined : { 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
              }}>
              <input
                type="checkbox"
                className="app-inbox__checkbox"
                checked={selectedUids.includes(email.uid)}
                onChange={e => { e.stopPropagation(); handleSelectOne(email.uid); }}
                aria-label={`Select email from ${senderName}`}
              />
              {showReadStatus && (
                <div
                  className={`status-dot ${unread ? 'green' : 'gray'}`}
                  aria-hidden="true"
                  title={unread ? 'Unread' : 'Read'}
                />
              )}
              <img 
                onClick={() => handleEmailClick(email)} 
                src={avatarUrl} 
                alt={senderName} 
                className="sender-avatar" 
                style={{ 
                  width: 32, 
                  height: 32, 
                  borderRadius: 16, 
                  flexShrink: 0 
                }} 
              />
                {isMobile && (
                  <div className="app-inbox__item-sender-row">
                    <span className="app-inbox__item-sender">
                      {activeFolder === 'Sent' || activeFolder === 'Drafts' || activeFolder === 'Outbox' 
                        ? `To: ${toName}` 
                        : (email.lastReply ? email.lastReply.from.name : senderName)}
                    </span>
                    <div className="app-inbox__item-meta">
                      <span className="app-inbox__item-time">
                        {formatDate(email)}
                      </span>
                      <button
                        type="button"
                        className="app-inbox__item-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === email.uid ? null : email.uid);
                        }}
                        aria-label="Email actions"
                      >
                        <MoreHorizontal size={20} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isMobile ? (
                <div 
                  className="app-inbox__item-body"
                  onClick={() => handleEmailClick(email)} 
                >
                  <div className="app-inbox__item-subject">
                    {email.envelope?.subject || '(No Subject)'}
                    {email.isReply && email.mainEmail && email.mainEmail.folder.name !== activeFolder && (
                      <>
                        {' '}
                        <FolderBadge 
                          folderName={email.mainEmail.folder.name} 
                          className="inline-flex flex-shrink-0"
                        />
                      </>
                    )}
                  </div>
                  <div className="app-inbox__item-preview">
                    {email.body?.text ? email.body.text.substring(0, 120) : 
                     email.body?.html ? extractTextFromHTML(email.body.html).substring(0, 120) : 
                     email.body?.raw ? email.body.raw.substring(0, 120) : 
                     'No preview available'}
                  </div>
                  {(hasAttachment || email.replyCount > 0) && (
                  <div className="app-inbox__item-tags">
                    {hasAttachment && (
                      <span className="app-inbox__item-tag">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49" />
                        </svg>
                        Attachment
                      </span>
                    )}
                    {email.replyCount > 0 && (
                      <span className="app-inbox__item-tag app-inbox__item-tag--replies">
                        {email.replyCount} {email.replyCount === 1 ? 'reply' : 'replies'}
                      </span>
                    )}
                  </div>
                  )}
                </div>
              ) : (
              <div 
                onClick={() => handleEmailClick(email)} 
                className="email-content" 
                style={{ 
                  display: 'flex',
                  flex: 1,
                  minWidth: 0, // Important for text overflow to work
                  gap: '12px'
                }}
              >
                {activeFolder === 'Sent' || activeFolder === 'Drafts' || activeFolder === 'Outbox' ? (
                  <div className="sender-name" style={{ 
                    fontSize: 14, 
                    width: '150px', 
                    flexShrink: 0,
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap'
                  }}>To: {toName}</div>
                ) : (
                  <div className="sender-name" style={{ 
                    fontSize: 14, 
                    width: '150px',
                    flexShrink: 0,
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap'
                  }}>{email.lastReply ? email.lastReply.from.name : senderName}</div>
                )}

                                  <div className="email-content-wrapper" style={{ 
                    fontSize: 14, 
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                                          <span style={{ fontWeight: 600 }}>
                      {email.lastReply ? email.lastReply.subject : (email.envelope?.subject || '(No Subject)')}
                    </span>
                    {' - '}
                    <span style={{ color: '#6B7280' }}>
                      {email.lastReply ? (
                        <span style={{ color: '#4B5563', fontWeight: 500 }}>
                          {email.lastReply.body_text || email.lastReply.from.email}: 
                        </span>
                      ) : (
                        email.body?.text ? email.body.text.substring(0, 100) : 
                        email.body?.html ? extractTextFromHTML(email.body.html).substring(0, 100) : 
                        email.body?.raw ? email.body.raw.substring(0, 100) : 
                        'No preview available'
                      )}
                    </span>
                    </div>
                    {email.isReply && email.mainEmail && email.mainEmail.folder.name !== activeFolder && (
                      <FolderBadge 
                        folderName={email.mainEmail.folder.name} 
                        className="flex-shrink-0"
                      />
                    )}
                  </div>

                <div className="email-actions" style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '150px', 
                  flexShrink: 0,
                  justifyContent: 'flex-end'
                }}>
                  <div className="hover-actions" style={{
                    display: 'none', // Hidden by default, shown on hover via CSS
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                            setActionLoading(email.uid, 'star', true);
                          await starEmail(folderName, email.uid, email.id, !isStarred);
                            setActiveMenu(null);
                        } catch (err) {
                          toast.error('Failed to update star');
                          } finally {
                            setActionLoading(email.uid, 'star', false);
                        }
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                          cursor: actionLoadingStates[email.uid]?.star ? 'wait' : 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                          color: isStarred ? '#f59e0b' : '#6B7280',
                          opacity: actionLoadingStates[email.uid]?.star ? 0.5 : 1
                      }}
                      title={isStarred ? "Unstar" : "Star"}
                        disabled={actionLoadingStates[email.uid]?.star}
                      >
                        {actionLoadingStates[email.uid]?.star ? (
                          <div className="loading-spinner" style={{ 
                            width: '16px', 
                            height: '16px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : isStarred ? <StarOff size={16} /> : <Star size={16} />}
                    </button>
                    <button
                        onClick={async (e) => {
                        e.stopPropagation();
                        if (!flags.includes('\\Seen')) {
                            try {
                              setActionLoading(email.uid, 'read', true);
                              await handleSingleMarkRead(email.uid, email.id);
                              setActiveMenu(null);
                            } finally {
                              setActionLoading(email.uid, 'read', false);
                            }
                        }
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                          cursor: flags.includes('\\Seen') ? 'default' : (actionLoadingStates[email.uid]?.read ? 'wait' : 'pointer'),
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: flags.includes('\\Seen') ? '#9CA3AF' : '#6B7280',
                          opacity: flags.includes('\\Seen') ? 0.5 : (actionLoadingStates[email.uid]?.read ? 0.5 : 1)
                      }}
                      title={flags.includes('\\Seen') ? "Already read" : "Mark as read"}
                        disabled={flags.includes('\\Seen') || actionLoadingStates[email.uid]?.read}
                      >
                        {actionLoadingStates[email.uid]?.read ? (
                          <div className="loading-spinner" style={{ 
                            width: '16px', 
                            height: '16px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : <MailCheck size={16} />}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                          setSelectedUids([]);
                        setShowMoveModal(true);
                          setSingleMoveUid(email.uid);
                          setActiveMenu(null);
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                          cursor: actionLoadingStates[email.uid]?.move ? 'wait' : 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#6B7280',
                          opacity: actionLoadingStates[email.uid]?.move ? 0.5 : 1
                      }}
                      title="Move to folder"
                        disabled={actionLoadingStates[email.uid]?.move}
                      >
                        {actionLoadingStates[email.uid]?.move ? (
                          <div className="loading-spinner" style={{ 
                            width: '16px', 
                            height: '16px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : <Move size={16} />}
                    </button>
                    <button
                        onClick={async (e) => {
                        e.stopPropagation();
                          try {
                            setActionLoading(email.uid, 'delete', true);
                            await handleSingleDelete(email);
                            setActiveMenu(null);
                          } finally {
                            setActionLoading(email.uid, 'delete', false);
                          }
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                          cursor: actionLoadingStates[email.uid]?.delete ? 'wait' : 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                          color: '#6B7280',
                          opacity: actionLoadingStates[email.uid]?.delete ? 0.5 : 1
                      }}
                      title="Delete"
                        disabled={actionLoadingStates[email.uid]?.delete}
                      >
                        {actionLoadingStates[email.uid]?.delete ? (
                          <div className="loading-spinner" style={{ 
                            width: '16px', 
                            height: '16px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                        ) : <Trash2 size={16} />}
                    </button>
                  </div>

                  {email.replyCount > 0 && (
                    <span style={{
                      padding: '2px 8px',
                      fontSize: '12px',
                      background: '#EFF6FF',
                      color: '#2563EB',
                      borderRadius: '9999px',
                      whiteSpace: 'nowrap'
                    }}>
                      {email.replyCount} {email.replyCount === 1 ? 'reply' : 'replies'}
                    </span>
                  )}
                  
                  {/* Static indicators */}
                    {hasAttachment && (
                    <svg 
                      className="attachment-icon" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ width: 20, height: 20 }}
                    >
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"></path>
                    </svg>
                  )}
                  
                </div>

                <div className="timestamp" style={{ 
                  width: '80px',
                  flexShrink: 0,
                  fontSize: 14,
                  color: '#6B7280',
                  textAlign: 'right'
                }}>
                  {formatDate(email)}
                </div>
              </div>
              )}

              {/* Mobile Action Menu */}
              {/* {isMobile && activeMenu === email.uid && (
                <div 
                  className="email-actions"
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '40%',
                    right: 16,
                    background: 'white',
                    borderRadius: 8,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    zIndex: 30,
                    padding: '8px 0',
                    minWidth: 150,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        setActionLoading(email.uid, 'star', true);
                        await starEmail(folderName, email.uid, email.id, !isStarred);
                        setActiveMenu(null);
                      } catch (err) {
                        toast.error('Failed to update star');
                      } finally {
                        setActionLoading(email.uid, 'star', false);
                      }
                    }}
                    disabled={actionLoadingStates[email.uid]?.star}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: 'none',
                      background: 'none',
                      cursor: actionLoadingStates[email.uid]?.star ? 'not-allowed' : 'pointer',
                      color: isStarred ? '#f59e0b' : '#374151',
                      fontSize: 14,
                      opacity: actionLoadingStates[email.uid]?.star ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[email.uid]?.star ? (
                      <LoaderIcon size={16} color={isStarred ? '#f59e0b' : '#3498db'} />
                    ) : (
                      isStarred ? <StarOff size={16} /> : <Star size={16} />
                    )}
                    {isStarred ? 'Unstar' : 'Star'}
                  </button>

                  {!flags.includes('\\Seen') && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!flags.includes('\\Seen')) {
                          try {
                            setActionLoading(email.uid, 'read', true);
                            await handleSingleMarkRead(email.uid, email.id);
                            setActiveMenu(null);
                          } finally {
                            setActionLoading(email.uid, 'read', false);
                          }
                        }
                      }}
                      disabled={actionLoadingStates[email.uid]?.read}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: 'none',
                        background: 'none',
                        cursor: actionLoadingStates[email.uid]?.read ? 'not-allowed' : 'pointer',
                        color: '#374151',
                        fontSize: 14,
                        opacity: actionLoadingStates[email.uid]?.read ? 0.7 : 1
                      }}
                    >
                      {actionLoadingStates[email.uid]?.read ? (
                        <LoaderIcon size={16} />
                      ) : (
                        <MailCheck size={16} />
                      )}
                      Mark as read
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUids([]);
                      setShowMoveModal(true);
                      setSingleMoveUid(email.uid);
                      setActiveMenu(null);
                    }}
                    disabled={actionLoadingStates[email.uid]?.move}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: 'none',
                      background: 'none',
                      cursor: actionLoadingStates[email.uid]?.move ? 'not-allowed' : 'pointer',
                      color: '#374151',
                      fontSize: 14,
                      opacity: actionLoadingStates[email.uid]?.move ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[email.uid]?.move ? (
                      <LoaderIcon size={16} />
                    ) : (
                      <Move size={16} />
                    )}
                    Move to
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        setActionLoading(email.uid, 'delete', true);
                        await handleSingleDelete(email);
                        setActiveMenu(null);
                      } finally {
                        setActionLoading(email.uid, 'delete', false);
                      }
                    }}
                    disabled={actionLoadingStates[email.uid]?.delete}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: 'none',
                      background: 'none',
                      cursor: actionLoadingStates[email.uid]?.delete ? 'not-allowed' : 'pointer',
                      color: '#dc2626',
                      fontSize: 14,
                      opacity: actionLoadingStates[email.uid]?.delete ? 0.7 : 1
                    }}
                  >
                    {actionLoadingStates[email.uid]?.delete ? (
                      <LoaderIcon size={16} color="#dc2626" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Delete
                  </button>
                </div>
              )} */}
            </div>
          );
        })}
      </div>
      {/* Move Modal */}
      {showMoveModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Move to Folder</h3>
            <div className="folder-list">
              {folders.map(folder => {
                const handleFolderClick = async () => {
                  if (folder.name === activeFolder) return;
                  
                  setMovingFolder(folder.name);
                  try {
                    if (selectedUids.length > 0) {
                      await handleMove(folder.name);
                    } else if (singleMoveUid !== null) {
                      await handleSingleMove(singleMoveUid, folder.name);
                    }
                  } finally {
                    setMovingFolder(null);
                  }
                };

                const isMovingToThisFolder = movingFolder === folder.name;

                return (
                <button
                  key={folder.name}
                    onClick={handleFolderClick}
                    disabled={movingFolder !== null || folder.name === activeFolder}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                      cursor: movingFolder !== null ? 'wait' : (folder.name === activeFolder ? 'not-allowed' : 'pointer'),
                    opacity: folder.name === activeFolder ? 0.5 : 1,
                    border: 'none',
                    background: 'none',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <Folder size={16} />
                  {folder.name}
                    {isMovingToThisFolder && (
                      <div className="loading-spinner" style={{ 
                        marginLeft: 'auto',
                        width: '16px',
                        height: '16px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    )}
                </button>
                );
              })}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setSingleMoveUid(null);
                  setMovingFolder(null);
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Compose Button for Mobile */}
      {isMobile && onCompose && (
        <button
          onClick={onCompose}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#8D00E4',
            color: 'white',
            border: 'none',
  display: 'flex',
  alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
            zIndex: 999,
            transition: 'transform 0.2s ease, background-color 0.2s ease',
            WebkitTapHighlightColor: 'transparent', // Remove tap highlight on mobile
            outline: 'none' // Remove focus outline
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#7c00d1';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#8D00E4';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onTouchStart={e => {
            e.currentTarget.style.backgroundColor = '#7c00d1';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onTouchEnd={e => {
            e.currentTarget.style.backgroundColor = '#8D00E4';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <PenSquare size={24} style={{ strokeWidth: 2.5 }} />
        </button>
      )}

      {/* Sync Options Modal */}
      <Modal open={showSyncOptions} onOpenChange={setShowSyncOptions}>
        <ModalContent>
          <ModalTitle>Sync Options</ModalTitle>
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => {
                  setSyncType('last');
                  setCurrentAction('sync');
                  setRefreshKey(k => k + 1);
                  setShowSyncOptions(false);
                  toast.success('Syncing from last sync...');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8d00e4'
                }}>
                  <RotateCw size={16} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                    Sync from Last Sync
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Only sync new emails since your last synchronization
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#8d00e4',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>Last synced: {lastSyncTime}</span>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setSyncType('all');
                  setCurrentAction('sync');
                  setRefreshKey(k => k + 1);
                  setShowSyncOptions(false);
                  toast.success('Syncing all emails...');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8d00e4'
                }}>
                  <RefreshCw size={16} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                    Sync All Emails
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Synchronize all emails in this folder
                  </div>
                </div>
              </button>
            </div>
          </div>
        </ModalContent>
      </Modal>

      {/* {errorState && (
        <div className="error-state">
          <p>{errorState}</p>
          {retryCount < 3 && (
            <button onClick={retryFn}>
              Retry {retryCount > 0 ? `(${retryCount}/3)` : ''}
            </button>
          )}
        </div>
      )} */}
    </div>
  );
});

export default InboxList;
